import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MdBugReport,
  MdMic,
  MdMicOff,
  MdNotifications,
  MdPlayArrow,
  MdVolumeUp,
} from 'react-icons/md';
import { useAppSSE } from 'contexts/AppSSEContext';
import { useMessaging } from '../contexts/MessagingContext';
import {
  getMessengerNotificationPermission,
  requestMessengerNotificationPermission,
} from '../utils/messengerNotificationUtils';
import styles from './AudioSettingsModal.module.css';

const IS_DEV = process.env.NODE_ENV === 'development';

function useMicLevel(threshold, holdMs) {
  const [level, setLevel] = useState(0); // 0–100 RMS
  const [transmitting, setTransmitting] = useState(false);
  const [error, setError] = useState(null);

  // Keep latest threshold/holdMs accessible inside the rAF loop without re-registering the effect
  const thresholdRef = useRef(threshold);
  const holdMsRef = useRef(holdMs);
  useEffect(() => { thresholdRef.current = threshold; }, [threshold]);
  useEffect(() => { holdMsRef.current = holdMs; }, [holdMs]);

  useEffect(() => {
    let cancelled = false;
    let animFrame;
    let lastAboveAt = 0;
    let stopStream = () => {};

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }

        const ctx = new AudioContext();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);

        const buf = new Float32Array(analyser.fftSize);

        const tick = () => {
          analyser.getFloatTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
          const rms = Math.sqrt(sum / buf.length);
          const scaledLevel = Math.min(100, Math.round(rms * 400));
          setLevel(scaledLevel);

          const gate = thresholdRef.current;
          const isAbove = gate === 0 ? scaledLevel > 2 : scaledLevel >= gate;
          const now = performance.now();
          if (isAbove) lastAboveAt = now;
          setTransmitting(isAbove || (now - lastAboveAt) < holdMsRef.current);

          animFrame = requestAnimationFrame(tick);
        };
        tick();

        stopStream = () => {
          cancelAnimationFrame(animFrame);
          stream.getTracks().forEach((t) => t.stop());
          ctx.close();
        };
      })
      .catch((err) => {
        if (!cancelled) setError(err.name === 'NotAllowedError' ? 'Microphone access denied' : 'Could not access microphone');
      });

    return () => {
      cancelled = true;
      stopStream();
    };
  }, []); // only mount/unmount — threshold and holdMs read via refs

  return { level, transmitting, error };
}

export function AudioSettingsModal({ onClose, conversationId }) {
  const [mutedPref, setMutedPref] = useState(() => localStorage.getItem('voip_mic_muted') === 'true');
  const [noiseGate, setNoiseGate] = useState(() => parseFloat(localStorage.getItem('voip_noise_gate') ?? '0'));
  const [gateHold, setGateHold] = useState(() => parseFloat(localStorage.getItem('voip_noise_gate_hold') ?? '300'));
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [ringtonePlaying, setRingtonePlaying] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(() =>
    getMessengerNotificationPermission(),
  );
  const [devDelay, setDevDelay] = useState(0);
  const [devFiring, setDevFiring] = useState(null); // label of pending trigger
  const backdropRef = useRef(null);
  const ringtoneContextRef = useRef(null);
  const ringtoneTimerRef = useRef(null);
  const devTimerRef = useRef(null);

  const { publish } = useAppSSE();
  const { injectConversation } = useMessaging();

  const { level, transmitting, error: micError } = useMicLevel(noiseGate, gateHold);

  useEffect(() => {
    const refreshNotificationPermission = () => {
      setNotificationPermission(getMessengerNotificationPermission());
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') refreshNotificationPermission();
    };

    let mounted = true;
    let permissionStatus = null;
    window.addEventListener('focus', refreshNotificationPermission);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'notifications' })
        .then((status) => {
          if (!mounted) return;
          permissionStatus = status;
          permissionStatus.onchange = refreshNotificationPermission;
        })
        .catch(() => {});
    }

    return () => {
      window.removeEventListener('focus', refreshNotificationPermission);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      mounted = false;
      if (permissionStatus) permissionStatus.onchange = null;
    };
  }, []);

  const stopRingtone = useCallback((updateState = true) => {
    if (ringtoneTimerRef.current) {
      clearTimeout(ringtoneTimerRef.current);
      ringtoneTimerRef.current = null;
    }

    const context = ringtoneContextRef.current;
    ringtoneContextRef.current = null;

    if (context && context.state !== 'closed') {
      context.close().catch(() => {});
    }

    if (updateState) setRingtonePlaying(false);
  }, []);

  useEffect(() => () => stopRingtone(false), [stopRingtone]);
  useEffect(() => () => { if (devTimerRef.current) clearTimeout(devTimerRef.current); }, []);

  const DEV_CONVERSATION_ID = -9001;
  const devConversation = {
    id: DEV_CONVERSATION_ID,
    type: 'group',
    name: 'Dev Test Conversation',
    created_by: 0,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    metadata: {},
    participants: [],
    last_message: null,
    unread_count: 0,
  };

  const fireDevEvent = useCallback((label, eventType, payload, injectConv = false) => {
    if (devTimerRef.current) clearTimeout(devTimerRef.current);
    const fire = () => {
      if (injectConv) injectConversation(devConversation);
      publish(eventType, payload);
    };
    if (devDelay === 0) { fire(); return; }
    setDevFiring(label);
    devTimerRef.current = setTimeout(() => { fire(); setDevFiring(null); }, devDelay * 1000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publish, devDelay, injectConversation]);

  const handleMuteToggle = () => {
    const next = !mutedPref;
    setMutedPref(next);
    localStorage.setItem('voip_mic_muted', String(next));
  };

  const handleNoiseGateChange = (e) => {
    const val = parseFloat(e.target.value);
    setNoiseGate(val);
    localStorage.setItem('voip_noise_gate', String(val));
  };

  const handleGateHoldChange = (e) => {
    const val = parseFloat(e.target.value);
    setGateHold(val);
    localStorage.setItem('voip_noise_gate_hold', String(val));
  };

  const handleBackdropClick = (e) => {
    if (e.target === backdropRef.current) onClose();
  };

  const handleEnableNotifications = async () => {
    const currentPermission = getMessengerNotificationPermission();
    setNotificationPermission(currentPermission);
    if (currentPermission === 'granted' || currentPermission === 'unsupported') return;

    const permission = await requestMessengerNotificationPermission({
      retryBlocked: currentPermission === 'denied',
    });
    setNotificationPermission(permission);
  };

  const playRingtonePreview = () => {
    stopRingtone();

    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) return;

    const context = new AudioContextCtor();
    const masterGain = context.createGain();
    masterGain.gain.setValueAtTime(0.0001, context.currentTime);
    masterGain.gain.exponentialRampToValueAtTime(0.18, context.currentTime + 0.02);
    masterGain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 1.15);
    masterGain.connect(context.destination);

    [
      { start: 0, frequency: 659.25 },
      { start: 0.18, frequency: 880 },
      { start: 0.48, frequency: 659.25 },
      { start: 0.66, frequency: 880 },
    ].forEach(({ start, frequency }) => {
      const oscillator = context.createOscillator();
      const noteGain = context.createGain();
      const startAt = context.currentTime + start;
      const endAt = startAt + 0.14;

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(frequency, startAt);
      noteGain.gain.setValueAtTime(0.0001, startAt);
      noteGain.gain.exponentialRampToValueAtTime(1, startAt + 0.015);
      noteGain.gain.exponentialRampToValueAtTime(0.0001, endAt);

      oscillator.connect(noteGain);
      noteGain.connect(masterGain);
      oscillator.start(startAt);
      oscillator.stop(endAt + 0.02);
    });

    ringtoneContextRef.current = context;
    setRingtonePlaying(true);
    ringtoneTimerRef.current = setTimeout(stopRingtone, 1200);
  };

  const holdLabel = gateHold < 100 ? `${gateHold}ms` : `${(gateHold / 1000).toFixed(1)}s`;
  const notificationsEnabled = notificationPermission === 'granted';
  const notificationsBlocked = notificationPermission === 'denied';
  const notificationsUnsupported = notificationPermission === 'unsupported';
  const notificationActionLabel = notificationsEnabled
    ? 'Enabled'
    : notificationsBlocked
      ? 'Try again'
      : 'Enable';
  const notificationStatusText = notificationsEnabled
    ? 'Desktop notifications are enabled'
    : notificationsBlocked
      ? 'Notifications are blocked. Update site settings, then try again'
      : notificationsUnsupported
        ? 'Desktop notifications are not supported'
        : 'Allow alerts for incoming calls and messages';

  return (
    <div className={styles.backdrop} ref={backdropRef} onClick={handleBackdropClick} role="dialog" aria-modal aria-labelledby="messenger-settings-title">
      <div className={styles.modal}>
        <div className={styles.header}>
          <span id="messenger-settings-title" className={styles.title}>Messenger Settings</span>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className={styles.body}>

          {/* Mute preference */}
          <div className={styles.row}>
            <div className={styles.rowLabel}>
              {mutedPref ? <MdMicOff size={16} className={styles.rowIcon} /> : <MdMic size={16} className={styles.rowIcon} />}
              <div>
                <div className={styles.rowTitle}>Join muted</div>
                <div className={styles.rowDesc}>Start every call with your microphone muted</div>
              </div>
            </div>
            <button
              type="button"
              className={`${styles.toggle} ${mutedPref ? styles.toggleOn : ''}`}
              onClick={handleMuteToggle}
              aria-pressed={mutedPref}
              aria-label="Toggle join muted"
            >
              <span className={styles.toggleThumb} />
            </button>
          </div>

          <div className={styles.divider} />

          {/* Desktop notifications */}
          <div className={styles.row}>
            <div className={styles.rowLabel}>
              <MdNotifications size={16} className={styles.rowIcon} />
              <div>
                <div className={styles.rowTitle}>Desktop notifications</div>
                <div className={styles.rowDesc}>{notificationStatusText}</div>
              </div>
            </div>
            <button
              type="button"
              className={`${styles.previewBtn} ${notificationsEnabled ? styles.previewBtnActive : ''}`}
              onClick={handleEnableNotifications}
              disabled={notificationsEnabled || notificationsUnsupported}
              aria-label={notificationsBlocked ? 'Try enabling desktop notifications again' : 'Enable desktop notifications'}
              title={
                notificationsBlocked
                  ? 'Try again after allowing notifications in browser site settings'
                  : 'Enable desktop notifications'
              }
            >
              <MdNotifications size={16} />
              {notificationActionLabel}
            </button>
          </div>

          <div className={styles.divider} />

          {/* Ringtone preview */}
          <div className={styles.row}>
            <div className={styles.rowLabel}>
              <MdVolumeUp size={16} className={styles.rowIcon} />
              <div>
                <div className={styles.rowTitle}>Ringtone</div>
                <div className={styles.rowDesc}>Preview the incoming call sound</div>
              </div>
            </div>
            <button
              type="button"
              className={`${styles.previewBtn} ${ringtonePlaying ? styles.previewBtnActive : ''}`}
              onClick={playRingtonePreview}
              aria-label="Preview ringtone"
              title="Preview ringtone"
            >
              <MdPlayArrow size={16} />
              {ringtonePlaying ? 'Playing' : 'Preview'}
            </button>
          </div>

          <div className={styles.divider} />

          {/* Advanced settings */}
          <div className={styles.row} style={{ cursor: 'pointer' }} onClick={() => setShowAdvanced((v) => !v)}>
            <div className={styles.rowLabel}>
              <span className={`${styles.advancedChevron} ${showAdvanced ? styles.advancedChevronOpen : ''}`}>›</span>
              <div>
                <div className={styles.rowTitle}>Advanced settings</div>
                <div className={styles.rowDesc}>Noise gate and audio processing options</div>
              </div>
            </div>
          </div>

          {showAdvanced && (
            <div className={styles.section}>
              <div className={styles.rowLabel}>
                <MdMic size={16} className={styles.rowIcon} />
                <div>
                  <div className={styles.rowTitle}>Noise gate</div>
                  <div className={styles.rowDesc}>Suppress background noise below a volume threshold</div>
                </div>
              </div>

              {micError ? (
                <div className={styles.micError}>{micError}</div>
              ) : (
                <>
                  <div className={styles.meterWrap} aria-label={`Mic level ${level}%`}>
                    <div
                      className={`${styles.meterFill} ${transmitting ? styles.meterFillActive : ''}`}
                      style={{ width: `${level}%` }}
                    />
                    {noiseGate > 0 && (
                      <div className={styles.meterThreshold} style={{ left: `${noiseGate}%` }} title={`Threshold: ${noiseGate}`} />
                    )}
                  </div>

                  <div className={styles.speakingPreview}>
                    <span className={`${styles.previewDot} ${transmitting ? styles.previewDotActive : ''}`} />
                    <span className={`${styles.previewLabel} ${transmitting ? styles.previewLabelActive : ''}`}>
                      {transmitting ? 'Transmitting' : 'Not transmitting'}
                    </span>
                  </div>

                  <div className={styles.sliderRow}>
                    <span className={styles.sliderBound}>Off</span>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={noiseGate}
                      onChange={handleNoiseGateChange}
                      className={styles.slider}
                      aria-label="Noise gate threshold"
                    />
                    <span className={styles.sliderBound}>High</span>
                  </div>

                  <div className={styles.holdRow}>
                    <span className={styles.holdLabel}>Gate hold</span>
                    <input
                      type="range"
                      min={0}
                      max={2000}
                      step={50}
                      value={gateHold}
                      onChange={handleGateHoldChange}
                      className={styles.slider}
                      aria-label="Gate hold time"
                    />
                    <span className={styles.holdValue}>{holdLabel}</span>
                  </div>
                </>
              )}
            </div>
          )}

          {IS_DEV && (
            <>
              <div className={styles.divider} />
              <div className={styles.devSection}>
                <div className={styles.devHeader}>
                  <MdBugReport size={14} />
                  Dev triggers
                </div>

                <div className={styles.devDelayRow}>
                  <span className={styles.devDelayLabel}>Pre-fire delay</span>
                  <input
                    type="number"
                    min={0}
                    max={30}
                    step={1}
                    value={devDelay}
                    onChange={(e) => setDevDelay(Math.max(0, parseFloat(e.target.value) || 0))}
                    className={styles.devDelayInput}
                    aria-label="Pre-fire delay in seconds"
                  />
                  <span className={styles.devDelayUnit}>s</span>
                </div>

                <div className={styles.devButtons}>
                  <button
                    type="button"
                    className={styles.devBtn}
                    onClick={() => fireDevEvent('Incoming call', 'call-started', {
                      roomId: `dev-room-${Date.now()}`,
                      conversationId: conversationId ?? null,
                      roomName: 'Dev Call',
                      createdByName: 'Dev User',
                    })}
                  >
                    {devFiring === 'Incoming call' ? `Firing…` : 'Incoming call'}
                  </button>

                  <button
                    type="button"
                    className={styles.devBtn}
                    onClick={() => fireDevEvent('Message', 'message-sent', {
                      conversationId: DEV_CONVERSATION_ID,
                      message: {
                        id: Date.now(),
                        content: 'Dev test message',
                        sent_at: new Date().toISOString(),
                        sender_id: 0,
                        content_type: 'text',
                      },
                    }, true)}
                  >
                    {devFiring === 'Message' ? 'Firing…' : 'Message'}
                  </button>

                  <button
                    type="button"
                    className={styles.devBtn}
                    onClick={() => fireDevEvent('Missed call', 'message-sent', {
                      conversationId: DEV_CONVERSATION_ID,
                      message: {
                        id: Date.now(),
                        content: 'Missed voice call',
                        sent_at: new Date().toISOString(),
                        sender_id: 0,
                        content_type: 'voice-call',
                        metadata: { status: 'missed' },
                      },
                    }, true)}
                  >
                    {devFiring === 'Missed call' ? 'Firing…' : 'Missed call'}
                  </button>
                </div>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
